import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useUILocale } from '../hooks/useUILocale';
import { useHreflangHead } from '../hooks/useHreflangHead';
import { withLocale } from '../locale';

const PrivacyPage: React.FC = () => {
  const uiLocale = useUILocale();
  const href = (p: string) => withLocale(uiLocale, p);
  useHreflangHead(uiLocale, '/privacy');
  return (
    <Layout title="Privacy Policy">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-primary-600 px-6 py-8 text-white">
            <h1 className="text-2xl font-bold">Privacy Policy</h1>
            <p className="text-primary-100 mt-1">Last updated: 25 April 2026</p>
          </div>

          <div className="p-6 space-y-6">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Introduction</h2>
              <p className="text-gray-600">
                StoryChain ("we", "our", or "us") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, and safeguard your information
                when you use our mobile application and website.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Information We Collect</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Information you provide:</h3>
                  <ul className="mt-1 text-gray-600 text-sm space-y-1">
                    <li className="flex gap-2"><span>•</span><span><strong>Email address</strong> — used for authentication</span></li>
                    <li className="flex gap-2"><span>•</span><span><strong>Display name</strong> — shown to other players</span></li>
                    <li className="flex gap-2"><span>•</span><span><strong>Story content</strong> — sentences you write in collaborative stories</span></li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Automatically collected:</h3>
                  <ul className="mt-1 text-gray-600 text-sm space-y-1">
                    <li className="flex gap-2"><span>•</span><span><strong>Device information</strong> — for push notifications</span></li>
                    <li className="flex gap-2"><span>•</span><span><strong>Usage data</strong> — to improve the app experience</span></li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">How We Use Your Information</h2>
              <ul className="text-gray-600 text-sm space-y-1">
                <li className="flex gap-2"><span>•</span><span>To authenticate your account</span></li>
                <li className="flex gap-2"><span>•</span><span>To enable collaborative storytelling with other users</span></li>
                <li className="flex gap-2"><span>•</span><span>To send push notifications about your turn in stories</span></li>
                <li className="flex gap-2"><span>•</span><span>To generate AI-powered final stories</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Data Sharing</h2>
              <p className="text-gray-600 text-sm">
                We do not sell your personal information. Your story content is shared only with
                other participants in the same story. We use third-party AI services (such as OpenAI,
                Anthropic, and xAI) to generate final stories — only the story content (not your
                personal info) is sent for processing.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Story Visibility &amp; Sharing</h2>
              <p className="text-gray-600 text-sm mb-2">
                Each finished story has a visibility setting that you, as a participant, can change
                from inside the app at any time:
              </p>
              <ul className="text-gray-600 text-sm space-y-1">
                <li className="flex gap-2"><span>•</span><span>
                  <strong>Public</strong> — the default. The story can appear in the public Explore feed
                  and be discovered by anyone. The displayed author names are the display names of the
                  participants (not their email addresses).
                </span></li>
                <li className="flex gap-2"><span>•</span><span>
                  <strong>Unlisted</strong> — the story is hidden from Explore. Only people you share
                  the direct link with can open it.
                </span></li>
              </ul>
              <p className="text-gray-600 text-sm mt-2">
                Switching a story from Public to Unlisted removes it from the Explore feed for everyone,
                immediately. The reverse is also true. The "Hide" action in your personal list, by
                contrast, only affects your own view — other participants and Explore are unaffected.
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Joining a story by code is also possible without an account. In that case the only
                personal data we associate with your entries is the display name you typed when joining,
                stored alongside an anonymous identifier so you can keep writing on the same device.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Content Moderation</h2>
              <p className="text-gray-600 text-sm">
                All user-submitted content is automatically reviewed by AI moderation systems
                before being accepted. Inappropriate, harmful, or offensive content is rejected.
                AI-generated final stories are also subject to content safety guidelines.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Data Retention</h2>
              <p className="text-gray-600 text-sm">
                Your account and stories are retained until you request deletion.
                You can delete your account at any time.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Your Rights</h2>
              <ul className="text-gray-600 text-sm space-y-1">
                <li className="flex gap-2"><span>•</span><span>Access your data</span></li>
                <li className="flex gap-2"><span>•</span><span>Delete your account and all associated data</span></li>
                <li className="flex gap-2"><span>•</span><span>Opt out of push notifications</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Account Deletion</h2>
              <p className="text-gray-600 text-sm">
                To delete your account and all associated data, visit{' '}
                <Link to={href('/privacy/remove')} className="text-primary-600 hover:underline">Account Deletion</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Contact Us</h2>
              <p className="text-gray-600 text-sm">
                If you have questions about this Privacy Policy, contact us at:{' '}
                <a href="mailto:privacy@storychain.fun" className="text-primary-600 hover:underline">privacy@storychain.fun</a>
              </p>
            </section>
          </div>
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          <Link to={href('/terms')} className="hover:underline">Terms of Service</Link>
        </p>
      </div>
    </Layout>
  );
};

export default PrivacyPage;
